"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  DensityChart,
  Probability,
} from "@/components/tools/stats-distributions/charts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { densityGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  exponentialBetween,
  exponentialCdf,
  exponentialMoments,
  exponentialPdf,
  exponentialQuantile,
  exponentialSurvival,
  memorylessCheck,
  rateFromMean,
} from "@/lib/tool-kit/stats-distributions-more/exponential";

/** English strings. A translated content file overrides these through `ui`. */
export const EXPONENTIAL_DISTRIBUTION_CALCULATOR_STRINGS = {
  knownLabel: "The parameter I know",
  knownRate: "Rate (λ, events per unit)",
  knownMean: "Mean (1/λ, units per event)",
  rateLabel: "Rate λ",
  meanLabel: "Mean 1/λ",
  xLabel: "Value x (time, distance or other units)",
  x2Label: "Upper end of a range (optional)",
  x2Hint: "Adds P(x ≤ X ≤ this value) to the results.",
  sLabel: "Time already waited, s (optional)",
  sHint: "Shows that the wait still to come does not depend on s.",
  pLabel: "Quantile: probability p (optional)",
  pHint: "Finds the x with P(X ≤ x) = p, such as 0.9 for the 90th percentile.",
  results: "Results",
  parameterLine: "λ = 1/mean = 1/{mean} = {rate}",
  parameterLineRate: "λ = {rate}, so the mean is 1/λ = {mean}",
  probability: "Probability",
  meaning: "Meaning",
  value: "Value",
  density: "Density at x: f(x) = λe^(−λx)",
  atMost: "at most {x} (also: less than {x})",
  atLeast: "more than {x} (also: at least {x}; the survival function)",
  between: "from {a} to {b}",
  densityNote: "A density, not a probability: it can exceed 1.",
  mean: "Mean (1/λ)",
  variance: "Variance (1/λ²)",
  sd: "Standard deviation (1/λ)",
  median: "Median (ln 2 / λ)",
  hazard: "Hazard rate (λ)",
  memoryHeading: "Memorylessness",
  memoryLine:
    "P(X > s + x | X > s) = P(X > {sum}) / P(X > {s}) = {numerator} / {denominator} = {conditional}",
  memoryLineShort: "P(X > s + x | X > s) = {conditional}",
  memoryCompare:
    "P(X > x) = P(X > {x}) = {unconditional}. The two are equal: having waited {s} already changes nothing about the wait still to come.",
  quantileHeading: "Quantile",
  quantileLine:
    "x = −ln(1 − p) / λ = −ln(1 − {p}) / {rate} = {x}. So P(X ≤ {x}) = {p}.",
  chartHeading: "Density curve",
  chartLabel:
    "Density curve of the exponential distribution with the area for your probability shaded",
  chartCaption:
    "Exponential density with λ = {rate}. The shaded area under the curve is {shaded}.",
  shadedAtMost: "P(X ≤ x)",
  shadedBetween: "P({a} ≤ X ≤ {b})",
  enterKnown: "Enter a rate or mean greater than 0.",
  enterX: "Enter x as a number of 0 or more.",
  enterX2: "Enter the upper end of the range as a number of 0 or more.",
  enterS: "Enter s as a number of 0 or more, with λ × s at most 700.",
  enterP: "Enter p as a number from 0 up to, but not including, 1.",
} as const;

type Strings = typeof EXPONENTIAL_DISTRIBUTION_CALCULATOR_STRINGS;
type Known = "rate" | "mean";

/** Beyond this e^(−λs) underflows, so the conditional ratio can no longer be shown. */
const MAX_LAMBDA_S = 700;

interface Inputs {
  rate: number;
  mean: number;
  x: number;
  x2: number | null;
  s: number | null;
  p: number | null;
}

interface Form {
  known: Known;
  value: string;
  x: string;
  x2: string;
  s: string;
  p: string;
}

const INITIAL: Form = {
  known: "mean",
  value: "4",
  x: "6",
  x2: "",
  s: "3",
  p: "0.9",
};

function optionalNumber(
  text: string,
  valid: (value: number) => boolean
): { given: boolean; value: number | null } {
  if (text.trim() === "") {
    return { given: false, value: null };
  }
  const value = parseNumberInput(text);
  return { given: true, value: value !== null && valid(value) ? value : null };
}

function readInputs(form: Form): Inputs | keyof Strings {
  const known = parseNumberInput(form.value);
  if (known === null || known <= 0 || !Number.isFinite(known)) {
    return "enterKnown";
  }
  const rate = form.known === "rate" ? known : rateFromMean(known);
  const mean = form.known === "mean" ? known : rateFromMean(known);
  const x = parseNumberInput(form.x);
  if (x === null || x < 0) {
    return "enterX";
  }
  const x2 = optionalNumber(form.x2, (value) => value >= 0);
  if (x2.given && x2.value === null) {
    return "enterX2";
  }
  const s = optionalNumber(
    form.s,
    (value) => value >= 0 && rate * value <= MAX_LAMBDA_S
  );
  if (s.given && s.value === null) {
    return "enterS";
  }
  const p = optionalNumber(form.p, (value) => value >= 0 && value < 1);
  if (p.given && p.value === null) {
    return "enterP";
  }
  return { rate, mean, x, x2: x2.value, s: s.value, p: p.value };
}

function ProbabilityTable({
  inputs,
  strings,
  fmt,
}: {
  inputs: Inputs;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { rate, x, x2 } = inputs;
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const xText = sig(x);
  const rows: { notation: string; meaning: string; value: number }[] = [
    {
      notation: `P(X ≤ ${xText})`,
      meaning: t(strings.atMost, { x: xText }),
      value: exponentialCdf(x, rate),
    },
    {
      notation: `P(X > ${xText})`,
      meaning: t(strings.atLeast, { x: xText }),
      value: exponentialSurvival(x, rate),
    },
  ];
  if (x2 !== null) {
    const a = sig(Math.min(x, x2));
    const b = sig(Math.max(x, x2));
    rows.push({
      notation: `P(${a} ≤ X ≤ ${b})`,
      meaning: t(strings.between, { a, b }),
      value: exponentialBetween(x, x2, rate),
    });
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.probability}
            </th>
            <th className={TH} scope="col">
              {strings.meaning}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.value}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.notation}>
              <td className={`${TD} whitespace-nowrap font-mono`}>
                {row.notation}
              </td>
              <td className={`${TD} text-muted-foreground`}>{row.meaning}</td>
              <td className={`${TD} text-right font-mono`}>
                <Probability fmt={fmt} value={row.value} />
              </td>
            </tr>
          ))}
          <tr>
            <td className={`${TD} whitespace-nowrap font-mono`}>
              {`f(${xText})`}
            </td>
            <td className={`${TD} text-muted-foreground`}>
              {strings.density}. {strings.densityNote}
            </td>
            <td className={`${TD} text-right font-mono`}>
              {sig(exponentialPdf(x, rate))}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Memoryless({
  inputs,
  s,
  strings,
  fmt,
}: {
  inputs: Inputs;
  s: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const prob = (value: number) =>
    fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const check = memorylessCheck(s, inputs.x, inputs.rate);
  const line =
    check.denominator > 0
      ? t(strings.memoryLine, {
          sum: sig(s + inputs.x),
          s: sig(s),
          numerator: prob(check.numerator),
          denominator: prob(check.denominator),
          conditional: prob(check.conditional),
        })
      : t(strings.memoryLineShort, { conditional: prob(check.conditional) });
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.memoryHeading}</h3>
      <p className="font-mono text-muted-foreground text-sm">{line}</p>
      <p className="text-muted-foreground text-sm">
        {t(strings.memoryCompare, {
          x: sig(inputs.x),
          s: sig(s),
          unconditional: prob(check.unconditional),
        })}
      </p>
    </div>
  );
}

function Quantile({
  inputs,
  p,
  strings,
  fmt,
}: {
  inputs: Inputs;
  p: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const x = exponentialQuantile(p, inputs.rate);
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.quantileHeading}</h3>
      <p className="font-mono text-muted-foreground text-sm">
        {t(strings.quantileLine, {
          p: sig(p),
          rate: sig(inputs.rate),
          x: sig(x),
        })}
      </p>
    </div>
  );
}

function Sketch({
  inputs,
  strings,
  fmt,
}: {
  inputs: Inputs;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { rate, x, x2 } = inputs;
  const low = x2 === null ? 0 : Math.min(x, x2);
  const high = x2 === null ? x : Math.max(x, x2);
  const geometry = useMemo(() => {
    const xMax = Math.max(5 / rate, high * 1.15);
    return densityGeometry({
      pdf: (value) => exponentialPdf(value, rate),
      xMin: 0,
      xMax,
      shaded: [[low, high]],
      markers: x2 === null ? [x] : [low, high],
    });
  }, [rate, x, x2, low, high]);
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const shaded =
    x2 === null
      ? strings.shadedAtMost
      : t(strings.shadedBetween, { a: sig(low), b: sig(high) });
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
      <DensityChart
        format={(value) => fmt(value, { maximumSignificantDigits: 3 })}
        geometry={geometry}
        label={strings.chartLabel}
      />
      <p className="text-muted-foreground text-sm">
        {t(strings.chartCaption, { rate: sig(rate), shaded })}
      </p>
    </div>
  );
}

function Results({
  inputs,
  form,
  strings,
  fmt,
}: {
  inputs: Inputs;
  form: Form;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const moments = exponentialMoments(inputs.rate);
  const parameterLine =
    form.known === "mean"
      ? t(strings.parameterLine, {
          mean: sig(inputs.mean),
          rate: sig(inputs.rate),
        })
      : t(strings.parameterLineRate, {
          rate: sig(inputs.rate),
          mean: sig(inputs.mean),
        });
  return (
    <>
      <p className="font-mono text-muted-foreground text-sm">{parameterLine}</p>
      <ProbabilityTable fmt={fmt} inputs={inputs} strings={strings} />
      <StatGrid>
        <StatCard label={strings.mean} value={sig(moments.mean)} />
        <StatCard label={strings.variance} value={sig(moments.variance)} />
        <StatCard label={strings.sd} value={sig(moments.sd)} />
        <StatCard label={strings.median} value={sig(moments.median)} />
        <StatCard label={strings.hazard} value={sig(moments.hazard)} />
      </StatGrid>
      {inputs.s === null ? null : (
        <Memoryless fmt={fmt} inputs={inputs} s={inputs.s} strings={strings} />
      )}
      {inputs.p === null ? null : (
        <Quantile fmt={fmt} inputs={inputs} p={inputs.p} strings={strings} />
      )}
      <Sketch fmt={fmt} inputs={inputs} strings={strings} />
    </>
  );
}

export default function ExponentialDistributionCalculatorWidget() {
  const strings = useToolUi(EXPONENTIAL_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [form, setForm] = useState<Form>(INITIAL);
  const update = (patch: Partial<Form>) =>
    setForm((current) => ({ ...current, ...patch }));
  const inputs = readInputs(form);

  return (
    <>
      <section className="space-y-5">
        <PillGroup
          label={strings.knownLabel}
          name="ed-known"
          onChange={(known) => update({ known })}
          options={[
            { value: "mean", label: strings.knownMean },
            { value: "rate", label: strings.knownRate },
          ]}
          value={form.known}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="ed-value"
            label={
              form.known === "rate" ? strings.rateLabel : strings.meanLabel
            }
            numeric
            onChange={(value) => update({ value })}
            value={form.value}
          />
          <TextField
            id="ed-x"
            label={strings.xLabel}
            numeric
            onChange={(x) => update({ x })}
            value={form.x}
          />
          <TextField
            hint={strings.x2Hint}
            id="ed-x2"
            label={strings.x2Label}
            numeric
            onChange={(x2) => update({ x2 })}
            value={form.x2}
          />
          <TextField
            hint={strings.sHint}
            id="ed-s"
            label={strings.sLabel}
            numeric
            onChange={(s) => update({ s })}
            value={form.s}
          />
          <TextField
            hint={strings.pHint}
            id="ed-p"
            label={strings.pLabel}
            numeric
            onChange={(p) => update({ p })}
            value={form.p}
          />
        </div>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof inputs === "string" ? (
          <Notice>{strings[inputs]}</Notice>
        ) : (
          <Results fmt={fmt} form={form} inputs={inputs} strings={strings} />
        )}
      </section>
    </>
  );
}
